import React, { useState } from 'react';
import { arrayOf, shape, string, func } from 'prop-types';

import { NamedLink, PrimaryButton, SecondaryButton } from '../../components';

import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import css from './MessagePanel.css';
import Modal from '../Modal/Modal';

const { Money } = sdkTypes;

const MessagePanel = props => {
  const { listing, otherUser, currentUser, intl, onPurchase, onManageDisableScrolling } = props;

  const [customPrice, setCustomPrice] = useState('');
  const [minAmountError, setMinAmountError] = useState(false);
  const [offerInProgress, setOfferInProgress] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const min = config.minimumOffer;

  const handleOfferSubmit = () => {
    setMinAmountError(false);
    const priceToSent = customPrice !== '' ? customPrice : listing.attributes.price.amount / 100;

    if (customPrice < min) {
      setMinAmountError(true);
      setOfferInProgress(false);
    } else {
      setOfferInProgress(true);
      onPurchase(listing, { price: priceToSent / (listing.attributes.price.amount / 100) });
      setIsModalOpen(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  if (!listing) {
    return null;
  }

  const listingTitle = listing?.attributes?.title;
  const listingImage = listing?.images[0].attributes.variants["square-small"].url;
  const price = new Money(listing.attributes.price.amount, 'MXN');
  const formattedPrice = formatMoney(intl, price);
  const authorDisplayName = otherUser.attributes.profile.displayName;
  const otherUserId = otherUser.id.uuid;
  const profileImageUrl = otherUser.profileImage?.attributes?.variants?.default?.url;
  const initials = authorDisplayName.split(' ').map(name => name[0]).join('');

  const isClosed = listing.attributes.publicData.available !== 'Disponible';
  const isSold = listing.attributes.publicData.sold;
  const isOwnListing = listing.author.id.uuid === currentUser.id.uuid;


  return (
    <div className={css.root}>
      {/* Item Info Container */}
      <div className={css.itemInfo}>
        {!isOwnListing && (
          <div className={css.authorInfoContainer}>
            <div className={css.authorWrapper}>
              <div className={css.authorImageAndDetails}>
                <NamedLink name="ProfilePage" params={{ id: otherUserId }} className={css.profileImageLink}>
                  <div className={css.authorImageContainer}>
                    {profileImageUrl ? (
                      <img 
                        src={profileImageUrl} 
                        alt={`${authorDisplayName}'s profile`} 
                        className={css.profileImage} 
                      />
                    ) : (
                      <div className={css.initials}>{initials}</div>
                    )}
                  </div>
                </NamedLink>
                <div className={css.authorDetails}>
                  <NamedLink name="ProfilePage" params={{ id: otherUserId }} className={css.authorNameLink}>
                    <span className={css.authorName}>{authorDisplayName}</span>
                  </NamedLink>
                  <span className={css.salesCount}>{props.salesCount} ventas</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={css.itemInfoContainer}>
          <NamedLink name="ListingPage" params={{ id: listing.id.uuid, slug: listingTitle }} className={css.itemInfoImage}>
            <img 
              src={listingImage} 
              alt={listingTitle} 
              className={css.itemImage} 
            />
          </NamedLink>
          <div className={css.itemInfoText}>
            <NamedLink name="ListingPage" params={{ id: listing.id.uuid, slug: listingTitle }}>
              <h2>{listingTitle}</h2>
            </NamedLink>
            <p>Total: {formattedPrice}</p>
          </div>
        </div>

        <div className={css.buttonContainer}>
          {!isClosed && !isSold && !isOwnListing && (
            <>
              <PrimaryButton className={css.buyButton} onClick={() => onPurchase(listing, { price: 1 })}>
                Comprar
              </PrimaryButton>
              <SecondaryButton className={`${css.buyButton} ${css.secondaryButton}`} onClick={openModal}>
                Ofertar
              </SecondaryButton>
            </>
          )}
        </div>
      </div>

      {/* Mobile Buttons */}
      {!isClosed && !isSold && !isOwnListing && (
        <div className={css.mobileButtonContainer}>
            <div className={css.mobileButtonsWrapper}>
              <SecondaryButton className={`${css.buyButton} ${css.secondaryButton}`} onClick={openModal}>
                Ofertar
              </SecondaryButton>
              <PrimaryButton className={css.buyButton} onClick={() => onPurchase(listing, { price: 1 })}>
                Comprar
              </PrimaryButton>
            </div>
        </div>
      )}


      {/* Offer Modal */}
      <Modal
        id="OfferModal"
        isOpen={isModalOpen}
        onClose={closeModal}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div>
          <h2 className={css.title}>Haz una oferta</h2>
          <input
            type="number"
            value={customPrice}
            onChange={e => setCustomPrice(e.target.value)}
            placeholder="Ingresa una cantidad a ofertar"
            min={min}
            className={css.offerInput}
          />
          <PrimaryButton className={css.buyButton} onClick={handleOfferSubmit} disabled={offerInProgress}>
            Enviar Oferta
          </PrimaryButton>
          {minAmountError && <p className={css.error}>El mínimo precio que se puede ofertar es de ${Number(min).toFixed(2)} MXN</p>}
        </div>
      </Modal>
    </div>
  );
};

MessagePanel.propTypes = {
  listing: shape({
    attributes: shape({
      title: string.isRequired,
      description: string,
      price: string.isRequired,
    }).isRequired,
  }).isRequired,
  otherUser: shape({
    id: string.isRequired,
    username: string.isRequired,
    profileImage: shape({
      attributes: shape({
        variants: shape({
          default: shape({
            url: string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  messages: arrayOf(
    shape({
      id: string.isRequired,
      message: string.isRequired,
      timestamp: string.isRequired,
    })
  ).isRequired,
  intl: shape({
    formatNumber: func.isRequired,
  }).isRequired,
  onPurchase: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default MessagePanel;